import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, rupee, todayISO } from "../lib/api";
import { useApp, useData } from "../lib/app";
import { AiChip, AiLoader, Chips, Empty, Header, MoneyNote, Sheet } from "../components/bits";
import { useT } from "../lib/i18n";

const WORK = ["Cleaning (jhadu-pocha)", "Bartan", "Cook", "Dhobi / ironing", "Driver", "Nanny", "Other"];
const iso = (y, m, d) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function AddHelper({ open, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", work: [], days_per_week: 6, monthly: "", paid_leaves: 2 });
  const t = useT();
  const save = async () => {
    try { await api.post("/helpers", { ...f, monthly: f.monthly ? Number(f.monthly) : null }); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Sheet open={open} onClose={onClose} testid="add-helper-sheet">
      <div className="space-y-3">
        <h3 className="font-display text-xl font-bold">{t("Add a helper")}</h3>
        <input data-testid="helper-name" className="ul-input" placeholder={t("First name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Chips options={WORK} value={f.work} onChange={(v) => setF({ ...f, work: v })} testid="helper-work" />
        <label className="block text-sm font-bold">Monthly amount as agreed (optional, only you see this)
          <input data-testid="helper-monthly" type="number" className="ul-input mt-1" value={f.monthly} onChange={(e) => setF({ ...f, monthly: e.target.value })} /></label>
        <label className="block text-sm font-bold">Paid leaves per month
          <input type="number" className="ul-input mt-1" value={f.paid_leaves} onChange={(e) => setF({ ...f, paid_leaves: Number(e.target.value) })} /></label>
        <p className="text-xs text-[#6B6680]">We never ask for a helper's phone number, address or ID.</p>
        <button data-testid="helper-save" disabled={!f.name} className="ul-btn ul-primary w-full" onClick={save}>{t("Add")}</button>
      </div>
    </Sheet>
  );
}

function Detail({ h, onChanged }) {
  const tr = useT();
  const t = todayISO();
  const [ym, setYm] = useState([Number(t.slice(0, 4)), Number(t.slice(5, 7))]);
  const month = iso(ym[0], ym[1], 1).slice(0, 7);
  const { data: note, refetch } = useData(`/helpers/${h.id}/note?month=${month}`);
  const dim = new Date(ym[0], ym[1], 0).getDate();
  const first = (new Date(ym[0], ym[1] - 1, 1).getDay() + 6) % 7;
  const toggle = async (day) => { await api.post(`/helpers/${h.id}/leave`, { date: day }); onChanged(); refetch(); };
  const shift = (n) => { const d = new Date(ym[0], ym[1] - 1 + n, 1); setYm([d.getFullYear(), d.getMonth() + 1]); };
  const payTask = async () => {
    const amount = note.monthly ? ` (${rupee(note.adjusted ?? note.monthly)} or ${rupee(note.monthly)}, you decide)` : "";
    try {
      await api.post("/tasks/confirm", { tasks: [{ title: `Pay ${h.name}${amount}`, category: "helper", due_date: iso(ym[0], ym[1], dim), source: "type" }] });
      toast.success(`'Pay ${h.name}' added 💜`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="helper-detail">
      <div className="ul-card p-4">
        <p className="font-display text-2xl font-extrabold">{h.name}</p>
        <p className="text-sm text-[#6B6680]">{(h.work || []).join(", ")}{h.monthly ? ` · ${rupee(h.monthly)} a month (as agreed)` : ""} · {h.paid_leaves} paid leaves</p>
      </div>
      <div className="ul-card p-4">
        <div className="flex justify-between items-center mb-3">
          <button className="ul-chip" onClick={() => shift(-1)} aria-label="previous month">‹</button>
          <p className="ul-label">{new Date(ym[0], ym[1] - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>
          <button className="ul-chip" onClick={() => shift(1)} aria-label="next month">›</button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 text-center text-xs font-bold text-[#9C97AE] mb-1">{"MTWTFSS".split("").map((x, i) => <span key={i}>{x}</span>)}</div>
        <div className="grid grid-cols-7 gap-1.5" data-testid="helper-calendar">
          {Array.from({ length: first }).map((_, i) => <span key={`e${i}`} />)}
          {Array.from({ length: dim }, (_, i) => {
            const day = iso(ym[0], ym[1], i + 1), leave = (h.leaves || []).includes(day), future = day > t;
            return (
              <button key={day} data-testid={`cal-day-${i + 1}`} onClick={() => toggle(day)}
                className="aspect-square rounded-lg text-sm font-bold transition-colors"
                style={{ background: leave ? "#FDE7C4" : future ? "#F7F5FE" : "#DFF6F3", color: leave ? "#8A6000" : future ? "#9C97AE" : "#0F8C80" }}>{i + 1}</button>
            );
          })}
        </div>
        <p className="text-xs text-[#6B6680] mt-2">{tr("🟩 came · 🟧 leave · tap a day to mark a leave")}</p>
      </div>
      {note && (
        <div className="ul-card p-4" data-testid="helper-note">
          <div className="mb-2"><AiChip text="Month-end note · You decide" /></div>
          <div className="flex justify-between py-1"><span className="text-[#6B6680]">{tr("Days worked")}</span><b data-testid="helper-days-worked">{note.days_worked} of {note.days_in_month}</b></div>
          <div className="flex justify-between py-1"><span className="text-[#6B6680]">{tr("Leaves")}</span><b>{note.leaves.length} ({note.paid} paid{note.extra ? ` + ${note.extra} extra` : ""})</b></div>
          {note.monthly > 0 && <>
            <div className="flex justify-between py-1"><span className="text-[#6B6680]">{tr("Full salary")}</span><b>{rupee(note.monthly)}</b></div>
            {note.extra > 0 && <div className="flex justify-between py-1 receipt-line pt-2"><span className="text-[#6B6680]">If you adjust for extra leave ({rupee(note.per_day)}/day)</span><b className="text-[#6A5AE0] font-display text-xl" data-testid="helper-adjusted">{rupee(note.adjusted)}</b></div>}
          </>}
          <button data-testid="helper-pay-task-btn" className="ul-btn ul-primary w-full mt-3" onClick={payTask}>Add 'Pay {h.name}' task</button>
          <p className="text-xs text-center text-[#6B6680] mt-2">{tr("The app never pays. You choose the amount.")}</p>
          <MoneyNote />
        </div>
      )}
    </div>
  );
}

export default function Helpers() {
  const { me } = useApp();
  const { data, isLoading, refetch } = useData("/helpers");
  const [sel, setSel] = useState(null);
  const [add, setAdd] = useState(false);
  const t = useT();
  const cur = useMemo(() => (data || []).find((h) => h.id === sel) || (data || [])[0], [data, sel]);
  if (!me.admin) return <Empty emoji="🔒" text="Only the home admin can see helpers." />;
  if (isLoading) return <AiLoader />;
  return (
    <div>
      <Header kicker="Family › Helpers" title="Helpers" right={<button data-testid="add-helper-btn" className="ul-btn ul-ghost mt-3" onClick={() => setAdd(true)}>{t("+ Add")}</button>} />
      {data.length > 1 && <div className="flex gap-2 mb-3">{data.map((h) => <button key={h.id} className="ul-chip" data-on={cur?.id === h.id} onClick={() => setSel(h.id)}>{h.name}</button>)}</div>}
      {cur ? <Detail key={cur.id} h={cur} onChanged={refetch} /> : <Empty emoji="🧹" text="No helpers added. Add one to track attendance." />}
      <p className="text-xs text-center text-[#6B6680] mt-4">Tip: say "Sunita Tuesday aur Friday nahi aayi" in Speak to log leaves.</p>
      <AddHelper open={add} onClose={() => setAdd(false)} onSaved={refetch} />
    </div>
  );
}
